import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { StatistikProduktivitetMedarbejder } from './models/StatistikProduktivitetMedarbejder';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Gruppe } from './models/gruppe';
import { UproduktiveSager } from './models/uproduktiveSager';
import { StatistikTidsforbrugViewModel } from './models/statistikTidsforbrugViewModel';
import { Afdeling } from './models/afdeling';
import { TimeBudgetView } from './models/timebudgetView';
import { StatistikIgangvaerende } from './models/StatistikIgangvaerende';
import { StatistikKunder } from './models/StatistikKunder';
import { StatistikDaekning } from './models/StatistikDaekning';
import { StatistikIgangvaerendeKunde } from './models/StatistikIgangvaerendeKunde';
import { StatistikYdelser } from './models/StatistikYdelser';
import { StatistikIgangvaerendeKundeFaktura } from './models/StatistikIgangvaerendeKundeFaktura';
import { StatistikNorm } from './models/StatistikNorm';
import { StatistikForventetTabListViewModel } from './models/StatistikForventetTabListViewModel';
import { EditForventetTabRequest } from './igangvaerende/statistik-igangvaerende-kunde-dato-modal/EditForventetTabRequest';
import { ProduktivitetFormler } from './models/ProduktivitetFormler';
import { ProduktivitetFormlerRequest } from './produktivitet/statistik-produktivitet/ProduktivitetFormlerRequest';
import { EmployeeTimeLogViewModelList } from './models/EmployeeTimeLogViewModel';
import { StatistikTidsforbrugPlantid } from './models/StatistikTidsforbrugPlantidViewModel';
import { StatistikTidsforbrugIkkeKunderelateretTidModalViewModel } from './models/StatistikTidsforbrugIkkeKunderelateretTidModalViewModel';
import { StatistikDaekningKunde } from './models/StatistikDaekningKunde';
import { StatistikDaekningKundeFaktura } from './models/StatistikDaekningKundeFaktura';
import { StatistikDaekningKundeBrugt } from './models/StatistikDaekningKundeBrugt';
import { StatistikNormPlantidModalViewModel } from './models/StatistikNormPlantidModalViewModel';
import { StatistikKunderPlanNorm } from './models/StatistikKunderPlanNorm';
import { StatistikTilafgangKunde } from './tilafgang/statistik-tilafgang/StatistikTilafgangKunde';
import { StatistikNetomslaFaktura } from './netomsla/statistik-netomsla/StatistikNetomslaFaktura';

@Injectable({
    providedIn: 'root'
})
export class StatistikService {
    private _visible = new BehaviorSubject<boolean>(true);
    visible$ = this._visible.asObservable();
    stepSubject = new BehaviorSubject<number>(1);
    step$ = this.stepSubject.asObservable();

    constructor(
        private http: HttpClient
    ) { }

    setStep(step: number) {
        this.stepSubject.next(step);
    }

    setVisible(visible: boolean) {
        this._visible.next(visible);
    }

    getStatistikProduktivitet(periode: { fra: string, til: string }): Observable<StatistikProduktivitetMedarbejder[]> {
        if (!periode) {
            return of([] as StatistikProduktivitetMedarbejder[]);
        }
        const url = `[api]/Statistik/produktivitet/[officeId]/${periode.fra}/${periode.til}`;
        return this.http.get<StatistikProduktivitetMedarbejder[]>(url);
    }

    getProduktivtetFormler(): Observable<ProduktivitetFormler[]> {
        const url = `[api]/Statistik/produktivitetformler/[officeId]`;
        return this.http.get<ProduktivitetFormler[]>(url);
    }

    postCreateProduktivtetFormler(produktivitetFormlerRequest: ProduktivitetFormlerRequest) {
        const url = `[api]/Statistik/postcreateproduktivitetformler/[officeId]`;
        return this.http.post<ProduktivitetFormler>(url, produktivitetFormlerRequest);
    }

    postEditProduktivtetFormler(produktivitetFormlerRequest: ProduktivitetFormlerRequest) {
        const url = `[api]/Statistik/posteditproduktivitetformler/[officeId]`;
        return this.http.post<ProduktivitetFormler>(url, produktivitetFormlerRequest);
    }

    postDeleteProduktivtetFormler(id: number) {
        const url = `[api]/Statistik/postdeleteproduktivitetformler/${id}`;
        // @ts-ignore
        return this.http.delete<ProduktivitetFormler>(url);
    }

    getEmployeeTimeLogView(id: number, gruppe: number, periode: { fra: string, til: string }) {
        if (!periode) {
            return of([] as EmployeeTimeLogViewModelList[]);
        }
        const url = `[api]/Statistik/produktivitetemployeetimelog/[officeId]/${id}/${gruppe}/${periode.fra}/${periode.til}`;
        return this.http.get<EmployeeTimeLogViewModelList[]>(url);
    }

    getTidsforbrug(periode: { fra: string, til: string }) {
        if (!periode) {
            return of([] as StatistikTidsforbrugViewModel[]);
        }
        const url = `[api]/Statistik/tidsforbrug/[officeId]/${periode.fra}/${periode.til}`;
        return this.http.get<StatistikTidsforbrugViewModel[]>(url);
    }

    getPlantidModalView(id: number, periode: { fra: string, til: string }) {
        if (!periode) {
            return of([] as StatistikTidsforbrugPlantid[]);
        }
        const url = `[api]/Statistik/plantidmodalview/${id}/${periode.fra}/${periode.til}`;
        return this.http.get<StatistikTidsforbrugPlantid[]>(url);
    }

    getTidsforbrugIkkeKunderelateretTid(id: number, pid, periode: { fra: string, til: string }) {
        if (!periode) {
            return of([] as StatistikTidsforbrugIkkeKunderelateretTidModalViewModel[]);
        }
        const url = `[api]/Statistik/tidsforbrugikkekunderelaterettidview/${id}/${pid}/${periode.fra}/${periode.til}`;
        return this.http.get<StatistikTidsforbrugIkkeKunderelateretTidModalViewModel[]>(url);
    }

    getStatistikUproduktivesager() {
        const url = `[api]/Statistik/tidsforbrug/uproduktivesager/[officeId]`;
        return this.http.get<UproduktiveSager[]>(url);
    }

    getGrupper() {
        const url = `[api]/statistik/grupper/[officeId]`;
        return this.http.get<Gruppe[]>(url);
    }

    getAfdelinger() {
        const url = `[api]/statistik/afdelinger/[officeId]`;
        return this.http.get<Afdeling[]>(url);
    }

    getKunder(date: string): Observable<StatistikKunder[]> {
        if (!date) {
            return of([] as StatistikKunder[]);
        }
            const url = `[api]/statistik/kunder/[officeId]/${date}`;
            return this.http.get<StatistikKunder[]>(url);
    }

    getKunderPlanNorm(date: string, medId: number): Observable<StatistikKunderPlanNorm> {
        if (!date || !medId) {
            return of(null);
        }
        const url = `[api]/statistik/kunderPlanNorm/[officeId]/${medId}/${date}`;
        return this.http.get<StatistikKunderPlanNorm>(url);
    }

    getTimebudgetByMedId(year: number, month: number, medID: number): Observable<TimeBudgetView[]> {
        const url = `[api]/statistik/timebudgetByMedId/${year}/${month}/${medID}`;
        return this.http.get<TimeBudgetView[]>(url);
    }

    getTimebudgetByKundeId(year: number, month: number, kundeID: number): Observable<TimeBudgetView[]> {
        const url = `[api]/statistik/timebudgetByKundeId/${year}/${month}/${kundeID}`;
        return this.http.get<TimeBudgetView[]>(url);
    }

    getTimebudgetByMedIdAndKundeId(year: number, month: number, medID: number, kundeID: number): Observable<TimeBudgetView[]> {
        const url = `[api]/statistik/timebudgetByMedIdAndKundeId/${year}/${month}/${medID}/${kundeID}`;
        return this.http.get<TimeBudgetView[]>(url);
    }

    getIgangvaerende(til: string, sa: number) {
        if (!til) {
            return of([] as StatistikIgangvaerende[]);
        }
        const url = `[api]/statistik/igangvaerende/[officeId]/${til}/${sa}`;
        return this.http.get<StatistikIgangvaerende[]>(url);
    }

    getIgangvaerendeKunde(til: string, kid: number) {
        if (!til) {
            return of([] as StatistikIgangvaerendeKunde[]);
        }
        const url = `[api]/statistik/igangvaerendekunde/[officeId]/${til}/${kid}`;
        return this.http.get<StatistikIgangvaerendeKunde[]>(url);
    }

    getIgangvaerendeKundeFaktura(til: string, kid: number, sagsid?: number | null)  {
        if (!til) {
            return of([] as StatistikIgangvaerendeKundeFaktura[]);
        }
        const url = `[api]/statistik/igangvaerendekundefaktura/[officeId]/${til}/${kid}/${sagsid}`;
        return this.http.get<StatistikIgangvaerendeKundeFaktura[]>(url);
    }

    postEditForventetTab(editForventetTabRequest: EditForventetTabRequest) {
        const url = '[api]/statistik/posteditforventettab';
        return this.http.post(url, editForventetTabRequest);
    }

    getForventedeTabListe(sagid: number) {
        const url = `[api]/statistik/forventettablist/[officeId]/${sagid}`;
        return this.http.get<StatistikForventetTabListViewModel[]>(url);
    }

    getDaekning(periode: { fra: string, til: string }, sa: number): Observable<StatistikDaekning[]> {
        if (!periode) {
            return of([] as StatistikDaekning[]);
        }
        const url = `[api]/statistik/daekning/[officeId]/${periode.fra}/${periode.til}/${sa ?? 0}`;
        return this.http.get<StatistikDaekning[]>(url);
    }

    getDaekningKunde(periode: { fra: string, til: string }, kid: number): Observable<StatistikDaekningKunde[]> {
        if (!periode) {
            return of([] as StatistikDaekningKunde[]);
        }
        const url = `[api]/statistik/daekningKunde/${periode.fra}/${periode.til}/${kid}`;
        return this.http.get<StatistikDaekningKunde[]>(url);
    }

    getDaekningKundeBrugtTimer(periode: { fra: string, til: string }, kid: number, sagsid: number): Observable<StatistikDaekningKundeBrugt[]> {
        if (!periode) {
            return of([] as StatistikDaekningKundeBrugt[]);
        }
        const url = `[api]/statistik/daekningKundeBrugtTimer/[officeId]/${periode.fra}/${periode.til}/${kid}/${sagsid}`;
        return this.http.get<StatistikDaekningKundeBrugt[]>(url);
    }

    getDaekningKundeBrugtKr(periode: { fra: string, til: string }, kid: number, sagsid: number): Observable<StatistikDaekningKundeBrugt[]> {
        if (!periode) {
            return of([] as StatistikDaekningKundeBrugt[]);
        }
        const url = `[api]/statistik/daekningKundeBrugtKr/[officeId]/${periode.fra}/${periode.til}/${kid}/${sagsid}`;
        return this.http.get<StatistikDaekningKundeBrugt[]>(url);
    }

    getDaekningKundeFaktureret(periode: { fra: string, til: string }, kid: number, sagsid: number): Observable<StatistikDaekningKundeFaktura[]> {
        if (!periode) {
            return of([] as StatistikDaekningKundeFaktura[]);
        }
        const url = `[api]/statistik/daekningKundeFaktureret/[officeId]/${periode.fra}/${periode.til}/${kid}/${sagsid}`;
        return this.http.get<StatistikDaekningKundeFaktura[]>(url);
    }

    getYdelser(periode: { fra: string, til: string }, la: number, ka: number, sa: number): Observable<StatistikYdelser[]> {
        if (!periode) {
            return of([] as StatistikYdelser[]);
        }
        const url = `[api]/statistik/ydelse/[officeId]/${periode.fra}/${periode.til}/${la ?? 0}/${ka ?? 0}/${sa ?? 0}`;
        return this.http.get<StatistikYdelser[]>(url);
    }

    getNorm(til: string): Observable<StatistikNorm[]> {
        if (!til) {
            return of([] as StatistikNorm[]);
        }
        const url = `[api]/statistik/norm/[officeId]/${til}`;
        return this.http.get<StatistikNorm[]>(url);
    }

    getPlantidModal(fra: string, medid: number): Observable<StatistikNormPlantidModalViewModel[]> {
        if (!fra) {
            return of([] as StatistikNormPlantidModalViewModel[]);
        }
        const url = `[api]/statistik/normPlantidModal/[officeId]/${fra}/${medid}`;
        return this.http.get<StatistikNormPlantidModalViewModel[]>(url);
    }

    getTilafgangNye(periode: { fra: string; til: string; }): Observable<StatistikTilafgangKunde[]> {
        const url = `[api]/statistik/tilafgangNye/[officeId]/${periode.fra}/${periode.til}`;
        return this.http.get<StatistikTilafgangKunde[]>(url);
    }

    getTilafgangMistede(periode: { fra: string; til: string; }): Observable<StatistikTilafgangKunde[]> {
        const url = `[api]/statistik/tilafgangMistede/[officeId]/${periode.fra}/${periode.til}`;
        return this.http.get<StatistikTilafgangKunde[]>(url);
    }

    getNetomsla(periode: { fra: string; til: string; }): Observable<StatistikNetomslaFaktura[]> {
        const url = `[api]/statistik/netomsla/[officeId]/${periode.fra}/${periode.til}`;
        return this.http.get<StatistikNetomslaFaktura[]>(url);
    }
}
